import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { DEPARTMENTS } from "../config/taxonomy";
import { useLanguage } from "../context/LanguageContext";

const leafTo = (item) => (item.soon ? null : `/market/collection/${item.handle}`);

// Desktop: accessible department bar + panel. Opens on hover AND click/keyboard.
export const MegaMenuDesktop = () => {
  const { ml, t } = useLanguage();
  const [open, setOpen] = useState(null); // department key
  const ref = useRef(null);
  const closeTimer = useRef(null);

  const close = useCallback(() => setOpen(null), []);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) close(); };
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [close]);

  const openDept = (k) => { clearTimeout(closeTimer.current); setOpen(k); };
  const scheduleClose = () => { closeTimer.current = setTimeout(() => setOpen(null), 180); };

  return (
    <nav ref={ref} className="hidden lg:block border-t border-black/5 relative" data-testid="mega-menu-desktop" aria-label="Shop by department">
      <div className="max-w-7xl mx-auto px-8 flex items-center gap-5 h-11">
        {DEPARTMENTS.map((d) => (
          <div key={d.key} className="h-full flex items-center" onMouseEnter={() => openDept(d.key)} onMouseLeave={scheduleClose}>
            <button
              type="button"
              data-testid={`mega-dept-${d.key}`}
              aria-haspopup="true"
              aria-expanded={open === d.key}
              onClick={() => setOpen((prev) => (prev === d.key ? null : d.key))}
              className={`inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-colors ${open === d.key ? "text-taven-gold" : "hover:text-taven-gold"}`}>
              {ml(d.label)}
              <ChevronDown className={`w-3 h-3 transition-transform ${open === d.key ? "rotate-180" : ""}`} />
            </button>
          </div>
        ))}
      </div>

      {open && (() => {
        const dept = DEPARTMENTS.find((x) => x.key === open);
        return (
          <div
            className="absolute left-0 right-0 top-full bg-taven-black text-white border-t border-taven-gold/40 shadow-2xl z-50"
            data-testid={`mega-panel-${open}`}
            onMouseEnter={() => openDept(open)} onMouseLeave={scheduleClose}>
            <div className="max-w-7xl mx-auto px-8 py-7">
              <p className="text-taven-gold text-[11px] font-bold uppercase tracking-[0.25em] mb-4">{ml(dept.label)}</p>
              <div className="grid grid-cols-4 gap-x-8 gap-y-2">
                {dept.items.map((it) => it.soon ? (
                  <span key={it.handle} data-testid={`mega-leaf-${it.handle}`}
                    className="py-1.5 text-sm text-white/45 flex items-center gap-2 cursor-default">
                    {ml(it.label)}
                    <span className="text-[9px] uppercase font-bold tracking-wider bg-taven-gold/20 text-taven-gold px-1.5 py-0.5 rounded">{t("comingSoon")}</span>
                  </span>
                ) : (
                  <Link key={it.handle} to={leafTo(it)} onClick={close} data-testid={`mega-leaf-${it.handle}`}
                    className="py-1.5 text-sm text-white/85 hover:text-taven-gold transition-colors">{ml(it.label)}</Link>
                ))}
              </div>
            </div>
          </div>
        );
      })()}
    </nav>
  );
};

// Mobile: drill-down accordion (department -> subcategories).
export const MegaMenuMobile = ({ onNavigate }) => {
  const { ml, t } = useLanguage();
  const [expanded, setExpanded] = useState(null);
  return (
    <div className="flex flex-col divide-y divide-black/5" data-testid="mega-menu-mobile">
      {DEPARTMENTS.map((d) => (
        <div key={d.key}>
          <button
            type="button"
            data-testid={`mega-m-dept-${d.key}`}
            aria-expanded={expanded === d.key}
            onClick={() => setExpanded(expanded === d.key ? null : d.key)}
            className="w-full flex items-center justify-between py-3 text-sm font-bold uppercase tracking-wider">
            {ml(d.label)}
            <ChevronRight className={`w-4 h-4 transition-transform ${expanded === d.key ? "rotate-90 text-taven-gold" : ""}`} />
          </button>
          {expanded === d.key && (
            <div className="pb-3 pl-3 flex flex-col" data-testid={`mega-m-panel-${d.key}`}>
              {d.items.map((it) => it.soon ? (
                <span key={it.handle} data-testid={`mega-m-leaf-${it.handle}`} className="py-2 text-sm text-black/40 flex items-center gap-2">
                  {ml(it.label)}
                  <span className="text-[9px] uppercase font-bold tracking-wider bg-taven-gold/15 text-taven-gold px-1.5 py-0.5 rounded">{t("comingSoon")}</span>
                </span>
              ) : (
                <Link key={it.handle} to={`/market/collection/${it.handle}`} onClick={onNavigate}
                  data-testid={`mega-m-leaf-${it.handle}`} className="py-2 text-sm text-black/75 hover:text-taven-gold">{ml(it.label)}</Link>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
